import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SupportBot } from "@/components/SupportBot";
import { LifeBuoy, Loader2, Check, MessageSquare, RotateCcw, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Support() {
  const [searchParams] = useSearchParams();
  const validCats = ["general", "return", "payment", "complaint"];
  const initialCat = validCats.includes(searchParams.get("cat")) ? searchParams.get("cat") : "general";
  const [form, setForm] = useState({ subject: "", message: "", category: initialCat, email: "" });
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error("Please fill in subject and message"); return; }
    setLoading(true);
    try { const { data } = await api.post("/support", { ...form, email: form.email || null }); setRef(data.ref); toast.success("Support case created"); }
    catch { toast.error("Could not submit. Try again."); } finally { setLoading(false); }
  };

  const CATS = [{ v: "general", l: "General Question", icon: MessageSquare }, { v: "return", l: "Return / Refund", icon: RotateCcw }, { v: "payment", l: "Payment Issue", icon: CreditCard }, { v: "complaint", l: "Complaint", icon: LifeBuoy }];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-8">
        <span className="h-14 w-14 rounded-2xl bg-emerald/10 text-emerald grid place-items-center mx-auto mb-4"><LifeBuoy size={28} /></span>
        <h1 className="font-display text-3xl font-bold">Customer Support</h1>
        <p className="text-muted-foreground mt-2">Ask a question, request a return, or track a resolution. We're here to help.</p>
      </div>

      <SupportBot />

      {ref ? (
        <div className="bg-card rounded-2xl border border-border p-8 text-center">
          <Check size={48} className="mx-auto text-emerald mb-3" />
          <h3 className="font-display font-bold text-xl">Case Created</h3>
          <p className="text-muted-foreground mt-2">Your reference is <span className="font-mono-sn font-semibold text-ink">{ref}</span>. Our team typically responds within a few hours.</p>
          <button onClick={() => setRef(null)} className="mt-5 text-emerald font-semibold">Submit another →</button>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-card rounded-2xl border border-border p-6 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CATS.map((c) => (
              <button type="button" key={c.v} onClick={() => setForm((f) => ({ ...f, category: c.v }))} className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-xs font-semibold transition-colors ${form.category === c.v ? "border-emerald bg-emerald/5" : "border-border"}`}>
                <c.icon size={18} className="text-emerald" /> {c.l}
              </button>
            ))}
          </div>
          <input value={form.subject} onChange={set("subject")} placeholder="Subject" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="support-subject" />
          <input value={form.email} onChange={set("email")} placeholder="Email (optional)" className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" />
          <textarea value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} className="w-full rounded-xl border border-border px-4 py-2.5 text-sm" data-testid="support-message" />
          <button disabled={loading} className="w-full bg-ink text-white rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="support-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null} Submit Case
          </button>
        </form>
      )}
    </div>
  );
}
